import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useDeleteScheduleMutation, useGetScheduleQuery, useSuiteSchedulerQuery } from "../../useQuery";
import { Days, SchedulerOptions } from "../../components-new/utils/constants";
import { PillRadio } from "../../components-new/common";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
  Paper,
  Stack,
  InputAdornment,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { useNotification } from "../../hooks";


const StyledLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "13px",
  fontWeight: 500,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: "15px 28px",
  borderRadius: "8px",
  textTransform: "capitalize",
  textAlign: "center",
  fontSize: "17px",
  fontWeight: 600,
  lineHeight: "20px",
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const getSubfreqArray = (subfreq) => {
  if (typeof subfreq === "string") {
    const trimmed = subfreq.split(",").map((day) => day.trim());
    return trimmed.length === Days.length ? [...Days] : trimmed;
  }
  return [];
};

function getStyles(day, schedule, theme) {
  return {
    fontWeight: schedule?.includes(day)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

const ScheduleDisplay = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
}));

const ScheduleItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const ScheduleLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  minWidth: '120px',
  fontWeight: 500,
}));

const ScheduleValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
}));

const Scheduler = ({ suiteId }) => {
  const theme = useTheme();
  const notify = useNotification();

  const { data: scheduleData, refetchWithParams: getSchedule } = useGetScheduleQuery();
  const { refetchWithParams: updateSchedule } = useSuiteSchedulerQuery();
  const { mutate: deleteSchedule } = useDeleteScheduleMutation({
    onSuccess: () => {
      notify("Scheduler is Successfully Deleted", "success");
      getSchedule(suiteId); // Refresh current schedule after deletion
    },
    onError: (error) => {
      notify(error.message || "Failed to delete schedule", "error");
    }
  });

  const [currentSchedule, setCurrentSchedule] = useState(null); // For displaying current schedule
  const [formSchedule, setFormSchedule] = useState(null); // For form changes

  useEffect(() => {
    getSchedule(suiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suiteId]);

  useEffect(() => {
    if (scheduleData) {
      setCurrentSchedule(scheduleData);
      setFormSchedule(scheduleData); // Initialize form with current schedule
    }
  }, [scheduleData]);

  const handleSchedulerChange = (event) => {
    const newFrequency = event.target.value;
    setFormSchedule((prevSchedule) => ({
      ...prevSchedule,
      frequency: newFrequency,
    }));
  };

  const handleTimeChange = (newTime) => {
    const formattedTime = dayjs(newTime).format("HH:mm:ss.0");
    setFormSchedule((prevSchedule) => ({
      ...prevSchedule,
      scheduletime: formattedTime,
    }));
  };

  const handleDayChange = (event) => {
    const {
      target: { value },
    } = event;

    setFormSchedule((prevSchedule) => ({
      ...prevSchedule,
      scheduletime: `${prevSchedule.scheduletime}.0`,
      subfreq: value.join(","),
    }));
  };

  const handleDateChange = (newDate) => {
    setFormSchedule((prevSchedule) => {
      return {
        ...prevSchedule,
        scheduletime: `${prevSchedule.scheduletime}.0`,
        scheduledate: dayjs(newDate).format("YYYY-MM-DD"),
      };
    });
  };

  const handleIntervalChange = (event) => {
    const value = event.target.value;
    // Only allow numbers between 1 and 60
    if (value === '' || (Number(value) >= 1 && Number(value) <= 60)) {
      setFormSchedule((prevSchedule) => ({
        ...prevSchedule,
        repeatInterval: value === '' ? '' : Number(value),
      }));
    }
  };

  const handleSubmitSchedule = () => {
    updateSchedule(formSchedule, suiteId).then(() => {
      getSchedule(suiteId); // Refresh current schedule after successful update
      notify("Scheduler is Successfully Saved", "success");
    });
  };

  const handleDeleteSchedule = () => {
    deleteSchedule(suiteId);
  };

  const getFrequencyLabel = (frequency) => {
    return SchedulerOptions.find(option => option.value === frequency)?.label || frequency;
  };

  const formatTime = (time) => {
    if (!time) return 'Not set';
    return dayjs(time, 'HH:mm:ss').format('hh:mm A');
  };

  return (
    <>
      {currentSchedule && (
        <ScheduleDisplay elevation={0}>
          <Typography variant="h6" gutterBottom>
            Current Schedule
          </Typography>
          <Stack spacing={1}>
            <ScheduleItem>
              <ScheduleLabel>Frequency:</ScheduleLabel>
              <ScheduleValue>{getFrequencyLabel(currentSchedule.frequency)}</ScheduleValue>
            </ScheduleItem>
            <ScheduleItem>
              <ScheduleLabel>Time:</ScheduleLabel>
              <ScheduleValue>{formatTime(currentSchedule.scheduletime)}</ScheduleValue>
            </ScheduleItem>
            {currentSchedule.repeatInterval && (
              <ScheduleItem>
                <ScheduleLabel>Repeat Every:</ScheduleLabel>
                <ScheduleValue>{currentSchedule.repeatInterval} minutes</ScheduleValue>
              </ScheduleItem>
            )}
            {currentSchedule.frequency === SchedulerOptions[1].value && (
              <ScheduleItem>
                <ScheduleLabel>Days:</ScheduleLabel>
                <ScheduleValue>
                  {getSubfreqArray(currentSchedule.subfreq).join(', ')}
                </ScheduleValue>
              </ScheduleItem>
            )}
            {currentSchedule.frequency === SchedulerOptions[2].value && (
              <ScheduleItem>
                <ScheduleLabel>Date:</ScheduleLabel>
                <ScheduleValue>
                  {currentSchedule.scheduledate ? dayjs(currentSchedule.scheduledate).format('MMMM D, YYYY') : 'Not set'}
                </ScheduleValue>
              </ScheduleItem>
            )}
          </Stack>
        </ScheduleDisplay>
      )}

      <Box>
        <StyledLabel>Select Frequency</StyledLabel>
        {SchedulerOptions.map((option) => (
          <PillRadio
            key={option?.value}
            value={option?.value}
            label={option?.label}
            checked={formSchedule?.frequency === option.value}
            onChange={handleSchedulerChange}
          />
        ))}
      </Box>
      <Box my={2} display={"flex"} alignItems={"center"} columnGap={2} flexWrap="wrap" rowGap={2}>
        <Box>
          <StyledLabel>Time</StyledLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              value={formSchedule?.scheduletime ? dayjs(formSchedule?.scheduletime, "HH:mm:ss") : null}
              onChange={handleTimeChange}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <StyledLabel>Repeat Every</StyledLabel>
          <TextField
            type="number"
            value={formSchedule?.repeatInterval || ''}
            onChange={handleIntervalChange}
            placeholder="Enter minutes"
            sx={{ width: '200px' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">mins</InputAdornment>,
              inputProps: { min: 1, max: 60 }
            }}
          />
        </Box>
        {formSchedule?.frequency === SchedulerOptions[1].value && (
          <Box>
            <FormControl sx={{ width: 257 }}>
              <StyledLabel>Select Day</StyledLabel>
              <Select
                multiple
                value={getSubfreqArray(formSchedule?.subfreq)}
                onChange={handleDayChange}
                MenuProps={MenuProps}
              >
                {Days.map((day) => (
                  <MenuItem
                    key={day}
                    value={day}
                    style={getStyles(day, getSubfreqArray(formSchedule?.subfreq), theme)}
                  >
                    {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        {formSchedule?.frequency === SchedulerOptions[2].value && (
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledLabel>Select Day</StyledLabel>
              <DatePicker
                value={formSchedule.scheduledate ? dayjs(formSchedule.scheduledate) : null}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </Box>
        )}
      </Box>
      <Box mb={2}>
        <Box display="flex" mt={2} columnGap={2}>
          <StyledButton sx={{ height: "50px" }} variant="contained" onClick={handleDeleteSchedule}>
            Delete
          </StyledButton>
          <StyledButton
            disabled={!formSchedule}
            sx={{ height: "50px" }}
            variant="contained"
            onClick={handleSubmitSchedule}
          >
            Submit
          </StyledButton>
        </Box>
      </Box>
    </>
  );
};

export default Scheduler;
